import * as React from "react"
import {
  Mic,
  Settings,
  User,
  ChevronRight,
  History,
  LogOut,
  CreditCard,
  FileText,
  Plus,
} from "lucide-react"

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  useSidebar,
} from "@/components/ui/sidebar"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { router, AppRoute } from '@/lib/router';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/I18nContext';
import { toast } from 'sonner';
import { apiManager } from '@/services/implementations/APIManager';

interface TranslatorSidebarProps extends React.ComponentProps<typeof Sidebar> {
  currentRoute?: AppRoute;
}

export function TranslatorSidebar({ currentRoute = AppRoute.HOME, ...props }: TranslatorSidebarProps) {
  const { userProfile, logout, refreshUserProfile } = useAuth();
  const { setOpenMobile, isMobile } = useSidebar();
  const { t } = useTranslation();

  const handleLogout = async () => {
    try {
      // 隐藏sidebar
      if (isMobile) {
        setOpenMobile(false);
      }

      // 🔑 调用logout，Auth0会自动处理重定向
      await logout();
    } catch (error) {
      console.error('登出失败:', error);
      toast.error('Logout failed, please try again');
    }
  };

  const handleAddTestTokens = async () => {
    try {
      await apiManager.addTestTokens();
      toast.success('测试 Token 添加成功！');
      // 刷新用户 profile 数据
      await refreshUserProfile();
      // 隐藏sidebar
      if (isMobile) {
        setOpenMobile(false);
      }
    } catch (error) {
      console.error('添加测试 Token 失败:', error);
      toast.error('添加测试 Token 失败，请稍后重试');
    }
  };
  
  const translatorData = {
    navMain: [
      {
        title: t('nav.translation'),
        route: AppRoute.HOME,
        icon: Mic,
        isActive: currentRoute === AppRoute.HOME,
        items: []
      },
      {
        title: t('nav.history'),
        route: AppRoute.CHATS,
        icon: History,
        isActive: currentRoute === AppRoute.CHATS,
        items: [
          // {
          //   title: "今日对话",
          //   url: "#",
          // },
          // {
          //   title: "历史记录",
          //   url: "#",
          // },
          // {
          //   title: "收藏夹",
          //   url: "#",
          // },
        ],
      },
      // {
      //   title: "语言设置",
      //   route: AppRoute.SETTINGS,
      //   icon: Languages,
      //   isActive: currentRoute === AppRoute.SETTINGS,
      //   items: []
      // },
      {
        title: t('purchase.title'),
        route: AppRoute.PURCHASE,
        icon: CreditCard,
        isActive: currentRoute === AppRoute.PURCHASE,
        items: []
      },
      {
        title: t('purchase.orderHistory'),
        route: AppRoute.ORDER_HISTORY,
        icon: FileText,
        isActive: currentRoute === AppRoute.ORDER_HISTORY,
        items: []
      },
    ],
    navSecondary: [
      // 测试环境才有这个入口
      ...(import.meta.env.DEV ? [{
        title: "添加测试Token",
        icon: Plus,
        action: "addTestTokens",
      }] : []),

      // {
      //   title: "音频设置",
      //   route: AppRoute.SETTINGS,
      //   icon: Volume2,
      // },
      // {
      //   title: "帮助",
      //   url: "#",
      //   icon: HelpCircle,
      // },
      // {
      //   title: "设置",
      //   route: AppRoute.SETTINGS,
      //   icon: Settings,
      // },
    ] as Array<{
      title: string;
      icon: any;
      route?: AppRoute;
      url?: string;
      action?: string;
    }>,
  }

  // 开发环境验证数据
  if (process.env.NODE_ENV === 'development') {
    translatorData.navSecondary.forEach((item, index) => {
      if (!item || typeof item !== 'object') {
        console.error(`❌ Invalid nav item at index ${index}:`, item);
        console.error('💡 Tip: Check for conditional rendering with && operator');
      }
      if (item && (!item.route && !item.url && !item.action)) {
        console.error('❌ Invalid nav item (no route, url, or action):', item);
      }
      if (item && item.url === undefined && !item.route && !item.action) {
        console.error('❌ Nav item has undefined url:', item);
      }
      if (item && !item.icon) {
        console.error('❌ Nav item missing icon:', item);
      }
    });
  }

  const handleNavigation = (route: AppRoute) => {
    router.navigate(route);
    // 隐藏sidebar
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar variant="floating" collapsible="icon" {...props}>
      <SidebarHeader className="border-b border-border/40">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild className="hover:bg-accent/50">
              <a href="#" className="group">
                  <img 
                    src="/icons/logo.png" 
                    alt="闪译猫 Logo"
                    className="size-8"
                  />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-neutral-900 dark:text-neutral-100">{t('home.title')}</span>
                  <span className="truncate text-xs text-muted-foreground">{t('home.subtitle')}</span>
                </div>
              </a>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      
      <SidebarContent className="px-0">
        {/* 主要功能导航 */}
        <SidebarGroup className="px-3">
          <SidebarGroupLabel className="text-xs font-medium text-muted-foreground/80 px-2 mb-2">
            {t('ui.siderbarTitle')}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {translatorData.navMain.map((item) => (
                <Collapsible key={item.title} asChild defaultOpen={item.isActive}>
                  <SidebarMenuItem>
                    <SidebarMenuButton 
                      asChild 
                      tooltip={item.title}
                      className="hover:bg-accent/50 data-[active=true]:bg-accent data-[active=true]:text-accent-foreground transition-all duration-200"
                      data-active={item.isActive}
                    >
                      <button 
                        onClick={() => handleNavigation(item.route)}
                        className="group w-full flex items-center"
                      >
                        <item.icon className="transition-colors group-hover:text-neutral-700 dark:group-hover:text-neutral-300" />
                        <span className="font-medium">{item.title}</span>
                      </button>
                    </SidebarMenuButton>
                    {item.items?.length ? (
                      <>
                        <CollapsibleTrigger asChild>
                          <SidebarMenuButton 
                            size="sm" 
                            className="ml-6 h-6 w-6 hover:bg-accent/30 data-[state=open]:bg-accent/40 transition-all duration-200"
                          >
                            <ChevronRight className="size-3 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-1">
                          <SidebarMenuSub className="ml-6 border-l border-border/40 pl-3 space-y-1">
                            {item.items?.map((subItem: any) => (
                              <SidebarMenuSubItem key={subItem.title}>
                                <SidebarMenuSubButton 
                                  asChild
                                  className="hover:bg-accent/30 text-muted-foreground hover:text-foreground transition-all duration-200"
                                >
                                  <a href={subItem.url}>
                                    <span className="text-sm">{subItem.title}</span>
                                  </a>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </>
                    ) : null}
                  </SidebarMenuItem>
                </Collapsible>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* 辅助功能 */}
        <SidebarGroup className="mt-auto px-3 pb-4">
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {translatorData.navSecondary
                .filter(Boolean)
                .map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton 
                    asChild 
                    size="sm" 
                    tooltip={item.title}
                    className="hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-all duration-200"
                  >
                    {item.action ? (
                        <button
                          onClick={() => {
                            if (item.action === 'addTestTokens') {
                              handleAddTestTokens();
                            }
                          }}
                          className="group w-full flex items-center"
                        >
                          <item.icon className="transition-colors" />
                          <span className="text-sm">{item.title}</span>
                        </button>
                    ) : item.route ? (
                      <button 
                        onClick={() => handleNavigation(item.route!)}
                        className="group w-full flex items-center"
                      >
                        <item.icon className="transition-colors" />
                        <span className="text-sm">{item.title}</span>
                      </button>
                    ) : (
                      <a href={item.url} className="group">
                        {/* TODO: 草你妈，就是这里！！！ */}
                        <item.icon className="transition-colors" />
                        <span className="text-sm">{item.title}</span>
                      </a>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="border-t border-border/40 p-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton size="lg" className="hover:bg-accent/50 transition-all duration-200">
                  <div className="bg-neutral-100 dark:bg-neutral-800 flex aspect-square size-8 items-center justify-center rounded-lg transition-colors group-hover:bg-neutral-200 dark:group-hover:bg-neutral-700">
                    {userProfile?.user?.avatar ? (
                      <img 
                        src={userProfile?.user?.avatar} 
                        alt={userProfile?.user?.name}
                        className="size-8 rounded-lg object-cover"
                      />
                    ) : (
                      <User className="size-4 text-neutral-600 dark:text-neutral-400" />
                    )}
                  </div>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium text-neutral-900 dark:text-neutral-100">
                      {userProfile?.user?.name || t('freeTrial.title')}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {userProfile?.user?.email || t('freeTrial.description')}
                    </span>
                  </div>
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" className="w-56">
                <DropdownMenuItem onClick={() => handleNavigation(AppRoute.SETTINGS)}>
                  <Settings className="mr-2 h-4 w-4" />
                  <span>{t('nav.settings')}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
                  <LogOut className="mr-2 h-4 w-4" />
                  <span>{t('auth.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
} 